/**
 * Utility for managing content management view and filter preferences in localStorage.
 * Provides type-safe storage and retrieval of user preferences for the ContentManagement component.
 */

import { SortConfig, StatusFilter, CalendarDateType } from '../types/contentManagement';

const STORAGE_KEY = 'content_management_preferences';

export interface ContentManagementPreferences {
  viewMode: 'cards' | 'calendar';
  search: string;
  contentType: string;
  statusFilter: StatusFilter;
  platformFilter: string;
  sortConfig: SortConfig;
  calendarDateType: CalendarDateType;
  currentPage: number;
}

/**
 * Default preferences when nothing is stored
 */
const DEFAULT_PREFERENCES: ContentManagementPreferences = {
  viewMode: 'cards',
  search: '',
  contentType: 'all',
  statusFilter: 'all',
  platformFilter: 'all',
  sortConfig: {
    key: 'created_at',
    direction: 'desc'
  },
  calendarDateType: 'scheduled_published',
  currentPage: 1
};

/**
 * Load preferences from localStorage with fallback to defaults
 */
export const loadContentPreferences = (): ContentManagementPreferences => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return DEFAULT_PREFERENCES;
    }

    const parsed = JSON.parse(stored);
    // Merge with defaults to ensure all properties exist (backwards compatibility)
    return {
      ...DEFAULT_PREFERENCES,
      ...parsed
    };
  } catch (error) {
    console.error('Error loading content preferences:', error);
    return DEFAULT_PREFERENCES;
  }
};

/**
 * Save preferences to localStorage
 */
export const saveContentPreferences = (preferences: Partial<ContentManagementPreferences>): void => {
  try {
    const current = loadContentPreferences();
    const updated = {
      ...current,
      ...preferences
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch (error) {
    console.error('Error saving content preferences:', error);
  }
};

/**
 * Clear all saved preferences
 */
export const clearContentPreferences = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing content preferences:', error);
  }
};

/**
 * Get a specific preference value
 */
export const getPreference = <K extends keyof ContentManagementPreferences>(
  key: K
): ContentManagementPreferences[K] => {
  const preferences = loadContentPreferences();
  return preferences[key];
};

/**
 * Set a specific preference value
 */
export const setPreference = <K extends keyof ContentManagementPreferences>(
  key: K,
  value: ContentManagementPreferences[K]
): void => {
  saveContentPreferences({ [key]: value });
};
